import React, { useState, useEffect } from "react";
import { saveSkill } from "../services/skillService";
import Select from "react-select";
import { FaMicrosoft, FaGoogle, FaPython, FaPaintBrush } from "react-icons/fa";
import { auth } from "../services/firebaseConfig"; // Firebase Authenticationの設定をインポート

// ツールごとのレベル設定
const levelExamples = {
  Excel: {
    初心者: "基本的な入力、セルの書式設定、簡単な表計算",
    中級者: "関数やグラフを使用したデータ分析",
    上級者: "ピボットテーブルやマクロの利用、データベースとの連携",
  },
  Word: {
    初心者: "基本的な文章作成、文字入力、簡単なフォーマット設定",
    中級者: "段落の整形、表や画像の挿入、文書の校正機能",
    上級者: "マクロやテンプレートの作成、高度なレイアウト設定",
  },
  Googleドキュメント: {
    初心者: "基本的な文章作成、共有機能の利用",
    中級者: "コメント機能、テンプレートの活用、簡単な共同編集",
    上級者: "アドオンの使用や高度な共同編集、スタイルの一括管理",
  },
  Python: {
    初心者: "基本的な構文（変数、条件分岐、ループ）の理解",
    中級者: "外部ライブラリの利用（例: pandas, matplotlib）、簡単なスクリプト作成",
    上級者: "オブジェクト指向プログラミング、高度なアルゴリズムの実装",
  },
  Googleスプレッドシート: {
    初心者: "セル入力、基本的な数式の使用",
    中級者: "条件付き書式やフィルタの利用、グラフの作成",
    上級者: "Google Apps Scriptの活用、データ連携の自動化",
  },
  Canva: {
    初心者: "テンプレートを使ったデザイン作成",
    中級者: "カスタムサイズのデザイン、オリジナル素材のアップロード",
    上級者: "ブランドキットの活用、高度なデザインテクニック",
  },
};

function SkillForm({ onSkillAdded }) {
  const [skill, setSkill] = useState("");
  const [level, setLevel] = useState("");
  const [selectedTool, setSelectedTool] = useState(null);
  const [levelDescription, setLevelDescription] = useState("");
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    // Firebase Authenticationから現在のユーザーのUIDを取得
    const user = auth.currentUser;
    if (user) {
      setUserId(user.uid); // ユーザーのUIDを設定
    }
  }, []);

  const handleToolChange = (selectedOption) => {
    setSelectedTool(selectedOption);
    setLevelDescription(
      levelExamples[selectedOption.value]?.初心者 || ""
    );
  };

  const handleLevelChange = (event) => {
    const selectedLevel = event.target.value;
    setLevel(selectedLevel);
    setLevelDescription(
      selectedTool ? levelExamples[selectedTool.value]?.[selectedLevel] : ""
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!skill.trim() || !level || !selectedTool || !userId) {
      alert("すべての項目を入力してください！");
      return;
    }

    const newSkill = {
      userId, // Firebaseから取得したUIDを使用
      name: `${skill} (${selectedTool.label})`,
      tool: selectedTool.value,
      level,
    };

    try {
      await saveSkill(newSkill); // データベースに保存
      onSkillAdded(newSkill); // 親コンポーネントへ通知
      // フォームを初期化
      setSkill("");
      setLevel("");
      setSelectedTool(null);
      setLevelDescription("");
    } catch (error) {
      console.error("スキルの保存に失敗しました:", error);
      alert("スキルの保存中にエラーが発生しました。");
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-lg mx-auto bg-white shadow-md rounded-md p-6"
    >
      <label className="block mb-4">
        <span className="block text-gray-700 font-medium mb-2">スキル名</span>
        <input
          type="text"
          value={skill}
          onChange={(e) => setSkill(e.target.value)}
          placeholder="例: データ分析"
          className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </label>
      <label className="block mb-4">
        <span className="block text-gray-700 font-medium mb-2">ツール</span>
        <Select
          value={selectedTool}
          onChange={handleToolChange}
          options={[
            { value: "Excel", label: "Excel", icon: <FaMicrosoft /> },
            { value: "Word", label: "Word", icon: <FaMicrosoft /> },
            { value: "Googleドキュメント", label: "Googleドキュメント", icon: <FaGoogle /> },
            { value: "Python", label: "Python", icon: <FaPython /> },
            { value: "Googleスプレッドシート", label: "Googleスプレッドシート", icon: <FaGoogle /> },
            { value: "Canva", label: "Canva", icon: <FaPaintBrush /> },
          ]}
          placeholder="ツールを選択..."
          className="react-select-container"
          classNamePrefix="react-select"
          getOptionLabel={(e) => (
            <div className="flex items-center">
              {e.icon && <span className="mr-2">{e.icon}</span>}
              {e.label}
            </div>
          )}
        />
      </label>
      <label className="block mb-4">
        <span className="block text-gray-700 font-medium mb-2">レベル</span>
        <select
          value={level}
          onChange={handleLevelChange}
          className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">レベルを選択</option>
          <option value="初心者">初心者</option>
          <option value="中級者">中級者</option>
          <option value="上級者">上級者</option>
        </select>
      </label>
      {level && (
        <div className="mb-4 p-4 bg-gray-100 rounded-md">
          <strong className="block text-gray-800 mb-2">レベルの見本:</strong>
          <p className="text-gray-600">{levelDescription}</p>
        </div>
      )}
      <button
        type="submit"
        className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600"
      >
        スキルを追加
      </button>
    </form>
  );
}

export default SkillForm;
